import {useEffect} from "react";
import {useQueryModal} from "@/hooks/useQueryModal";
import {useTableQuery} from "@/hooks/useTableQuery";
import {SortField, useNewsByTagSlug} from "@/hooks/useNews";
import {Link} from "react-router-dom";

export default function HotStoriesBanner() {
    const {searchParams, setParam} = useQueryModal();

    const {page, size, setPage} = useTableQuery<SortField>({
        searchParams,
        setParam,
        defaultPage: 0,
        defaultSize: 10,
        defaultSortBy: "publishedAt",
        defaultSortOrder: "desc",
    });

    const {data: result, isLoading, isError} = useNewsByTagSlug("hot-stories", {
        page, size, sortBy: "publishedAt", sortOrder: "desc",
    });

    const items = result?.items ?? [];

    useEffect(() => {
        if (page !== 0) setPage(0);
    }, [page, setPage]);

    if (items.length === 0 || isError) return null;

    return (
        <div className="relative bg-lime-300 text-black">
            <div className="ticker overflow-hidden whitespace-nowrap py-2 pl-28 pr-4">
                <div className="track inline-flex items-center gap-6">
                    {items.length === 0 && !isLoading ? (
                        <span className="opacity-80">No hot stories yet — check back soon.</span>
                    ) : (
                        <>
                            <TickerItems items={items}/>
                            <TickerItems items={items} ariaHidden/>
                        </>
                    )}
                </div>
            </div>
            <style>{`
        .ticker .track {
          animation: ticker var(--ticker-duration, 40s) linear infinite;
          will-change: transform;
        }
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); } /* two copies side-by-side */
        }
        /* Pause ONLY when hovering a link (not the empty area) */
        .ticker:has(a:hover) .track {
          animation-play-state: paused;
        }
        /* Respect reduced motion */
        @media (prefers-reduced-motion: reduce) {
          .ticker .track { animation: none; transform: none; }
        }
      `}</style>
        </div>
    );
}

function TickerItems({items, ariaHidden = false}: { items: any[]; ariaHidden?: boolean }) {
    return (
        <div className="inline-flex items-center gap-6 min-w-max" aria-hidden={ariaHidden}>
            {items.length === 0 ? (
                <span className="opacity-80">Loading hot stories…</span>
            ) : (
                items.map((n, i) => (
                    <Link to={`/nuus/${n.slug}`} key={(n.id ?? n.slug ?? i) + (ariaHidden ? "-link-dup" : "")}
                          aria-hidden={ariaHidden}>
                    <span
                        key={(n.id ?? n.slug ?? i) + (ariaHidden ? "-dup" : "")}
                        className="inline-flex items-center gap-2 hover:underline"
                        title={n.title}
                    >
                        <span className="inline-block size-1.5 rounded-full bg-black"/>
                        <span className="text-md font-medium line-clamp-1">{n.title}</span>
                    </span>
                    </Link>
                ))
            )}
        </div>
    );
}
